/**
 * 로그인 전역 Store 모듈
 * @module shared/store/useAppAuthStore
 */
import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { CryptoManager } from '@/shared/utils/crypto/CryptoManager.ts';

/**
 * 회원 정보 Interface
 */
interface Member {
  /**
   * 회원 ID
   */
  memberId: number;
  /**
   * SNS 제공자 (KAKAO, NAVER, GOOGLE, APPLE)
   */
  snsProviderCode: 'KAKAO' | 'NAVER' | 'GOOGLE' | 'APPLE';
  /**
   * 닉네임
   */
  nickname: string;
  /**
   * 이메일
   */
  email: string;
  /**
   * 프로필 이미지 파일명
   */
  profileImageFilename: string | null;
  /**
   * 선택된 캐릭터 ID
   */
  selectedCharacterId: number | null;
  /**
   * 총 포인트
   */
  totalPoints: number;
  /**
   * 연속 출석일수
   */
  consecutiveDays: number;
  /**
   * 활성화 여부
   */
  isActive: boolean;
  /**
   * 테스터 여부 (전체 문제 열람 및 일일 플레이 제한 우회)
   */
  isTester: boolean;
  /**
   * 레벨
   */
  level: number;
  /**
   * 가입일
   */
  createdAt: string;
  /**
   * 마지막 닉네임 변경 일자
   */
  lastNicknameUpdateDate: string;
  /**
   * 레벨 이미지 파일명
   */
  levelImageFilename: string;
}

/**
 * 로그인 Store Interface
 */
interface AuthState {
  /**
   * Access Token
   */
  accessToken: string | null;
  /**
   * Refresh Token
   */
  refreshToken: string | null;
  /**
   * 회원 정보
   */
  member: Member | null;
  /**
   * 로그인 여부
   */
  isLoggedIn: boolean;
  /**
   * 로그인 정보 저장 함수
   */
  login: (accessToken: string, refreshToken: string, member: Member) => void;
  /**
   * 회원 정보 업데이트 함수
   */
  updateMember: (member: Partial<Member>) => void;
  /**
   * 토큰 갱신 함수
   */
  updateTokens: (accessToken: string, refreshToken: string) => void;
  /**
   * 출석 체크
   */
  checkAttendance: () => void;
  /**
   * 로그아웃 함수
   */
  logout: () => void;
}

/**
 * AuthStore를 생성한다.
 */
const useAppAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      member: null,
      isLoggedIn: false,

      /**
       * 로그인 정보 저장
       */
      login: (accessToken, refreshToken, member) =>
        set({
          accessToken,
          refreshToken,
          member,
          isLoggedIn: true,
        }),

      /**
       * 회원 정보 업데이트 (부분 업데이트)
       */
      updateMember: updatedMember =>
        set(state => ({
          member: state.member ? { ...state.member, ...updatedMember } : null,
        })),

      /**
       * 토큰 갱신
       */
      updateTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),

      /**
       * 로그아웃
       */
      logout: () => {
        // CryptoManager 초기화
        CryptoManager.reset();

        // Store 초기화
        set({
          accessToken: null,
          refreshToken: null,
          member: null,
          isLoggedIn: false,
        });

        window.location.href = '/login';
      },

      /**
       * 출석 체크
       */
      checkAttendance: () => {
        const token = get().accessToken;
        if (token) {
          axios
            .post(
              '/api/app/attendance/check',
              { memberId: get().member?.memberId },
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            )
            .then(response => {
              get().updateMember(response.data.member);
            })
            .catch(error => {
              console.error('출석 체크 error:', error);
            });
        }
      },
    }),
    {
      name: 'tomtomi-auth-storage', // localStorage key
    },
  ),
);
export const getAuthState = () => useAppAuthStore.getState();

export default useAppAuthStore;
